import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { formatOrderDate } from '../utils/dateUtils';
import { useOrderEntryMode } from '../hooks/useOrderEntryMode';
import { PAYMENT_MODES } from '../utils/orderEntryMode';
import { XIcon, CopyIcon, CheckIcon, PrinterIcon, ClipboardIcon, AlertTriangleIcon, LoaderCircleIcon } from './icons/LucideIcons';

// The steps that only make sense in Shopify POS (FINDINGS Copy: they were
// shown to every shop). Shops that take payment here see only the general
// steps. Twigs (Shopify) reads exactly as before.
const shopifySteps = (orderNumber) => [
  { text: 'Open Shopify POS app', printHtml: '<strong>Open Shopify POS app</strong> on your device' },
  { text: 'Tap "Orders" in the bottom navigation', printHtml: 'Tap <strong>"Orders"</strong> in the bottom navigation' },
  { text: 'Tap "Drafts" at the top of the screen', printHtml: 'Tap <strong>"Drafts"</strong> at the top of the screen' },
  {
    text: `Search for order number: ${orderNumber}`,
    highlight: true,
    printHtml: `Search for order number: <strong style="background: #fef3c7; padding: 2px 6px; border-radius: 4px;">${orderNumber}</strong>`,
  },
  { text: 'Tap the order to open it', printHtml: 'Tap the order to open it' },
  { text: 'Tap "Load to Cart" or "Checkout"', printHtml: 'Tap <strong>"Load to Cart"</strong> or <strong>"Checkout"</strong>' },
];
const GENERAL_STEPS = [
  { text: 'Process payment with customer', printHtml: 'Process payment with customer' },
  { text: 'Complete the transaction', printHtml: 'Complete the transaction' },
];

const POSInstructions = ({ 
  isOpen, 
  orderData,
  onClose,
  onPrint
}) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [elapsedTime, setElapsedTime] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  // null until known; the steps wait for it rather than flash the wrong set.
  const entryMode = useOrderEntryMode();
  const isShopify = entryMode === PAYMENT_MODES.SHOPIFY;
  const steps = entryMode === null
    ? []
    : [...(isShopify ? shopifySteps(orderData?.orderNumber) : []), ...GENERAL_STEPS];

  // Generate QR code for order number
  useEffect(() => {
    if (isOpen && orderData?.orderNumber) {
      QRCode.toDataURL(orderData.orderNumber, {
        width: 200,
        margin: 2,
        color: {
          dark: '#166534', // floral-800
          light: '#FFFFFF'
        }
      }).then(url => {
        setQrCodeUrl(url);
      }).catch(err => {
        console.error('Failed to generate QR code:', err);
      });
    }
  }, [isOpen, orderData?.orderNumber]);

  // Timer for elapsed time since order creation
  useEffect(() => {
    if (!isOpen || !orderData?.createdAt) return;

    const updateElapsedTime = () => {
      const now = new Date();
      const created = new Date(orderData.createdAt);
      const diff = now - created;
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      
      if (minutes > 0) {
        setElapsedTime(`${minutes}m ${seconds}s ago`);
      } else {
        setElapsedTime(`${seconds}s ago`);
      }
    };

    updateElapsedTime();
    const interval = setInterval(updateElapsedTime, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, orderData?.createdAt]);

  const handleCopyOrderNumber = async () => {
    try {
      await navigator.clipboard.writeText(orderData.orderNumber);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy order number:', err);
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = orderData.orderNumber;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handlePrintInstructions = () => {
    const printContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #166534; text-align: center; border-bottom: 3px solid #166534; padding-bottom: 10px;">
          POS Processing Instructions
        </h1>
        
        <div style="background: #f0fdf4; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h2 style="color: #166534; margin-top: 0;">Order Information</h2>
          <p><strong>Order Number:</strong> <span style="font-size: 1.5em; color: #166534;">${orderData.orderNumber}</span></p>
          <p><strong>Customer:</strong> ${orderData.customerName}</p>
          <p><strong>Total:</strong> $${orderData.total}</p>
          <p><strong>Created:</strong> ${formatOrderDate(orderData.createdAt).text}</p>
        </div>

        <h2 style="color: #166534;">Step-by-Step POS Instructions</h2>
        <ol style="line-height: 1.8; font-size: 16px;">
          ${steps.map((step) => `<li>${step.printHtml}</li>`).join('\n          ')}
        </ol>

        <div style="border: 2px dashed #d1d5db; padding: 15px; margin: 20px 0; text-align: center;">
          <p style="margin: 0; color: #6b7280;">
            <strong>QR Code for Order Number:</strong><br>
            Use your POS scanner to quickly find this order
          </p>
        </div>

        <div style="background: #fef3c7; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0; color: #92400e;">
            <strong>Important:</strong> Verify order details with customer before processing payment.
          </p>
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>POS Instructions - Order ${orderData.orderNumber}</title>
          <style>
            @media print {
              body { margin: 0; }
              @page { margin: 1in; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    
    if (onPrint) onPrint();
  };

  if (!isOpen || !orderData) return null;

  // Dialog pattern: 16px radius, dialog shadow, no tinted header, title
  // 17px/600, actions right-aligned with one primary.
  return (
    <div className="fixed inset-0 bg-[color:var(--c-backdrop)] flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-lg max-w-2xl w-full max-h-full overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start gap-3 px-6 pt-6">
          <div>
            <h2 className="text-[17px] leading-6 font-semibold tracking-[-0.01em] text-gray-900">POS Processing Instructions</h2>
            {entryMode !== null && (
              <p className="mt-1 text-[13px] text-gray-600">
                {isShopify ? 'Process this order in Shopify POS' : 'Take payment for this order with the customer'}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost btn-sm -mr-2 px-1.5"
            aria-label="Close instructions"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="px-6 pb-6 pt-5 flex flex-col gap-5">
          {/* Order Information */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h3 className="text-[15px] font-semibold text-gray-900 mb-2">Order Details</h3>
                <div className="space-y-1 text-[13px] text-gray-600">
                  <p><strong className="font-semibold text-gray-900">Customer:</strong> {orderData.customerName}</p>
                  <p><strong className="font-semibold text-gray-900">Email:</strong> {orderData.customerEmail}</p>
                  <p><strong className="font-semibold text-gray-900">Total:</strong> <span className="tabular">${orderData.total}</span></p>
                  <p><strong className="font-semibold text-gray-900">Items:</strong> {orderData.itemCount || 'N/A'} item(s)</p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-xs text-gray-500 mb-2 tabular">Created {elapsedTime}</p>
                <div className="text-2xl font-semibold tabular text-gray-900 bg-white px-4 py-2 rounded-lg border border-gray-200">
                  {orderData.orderNumber}
                </div>
              </div>
            </div>
          </div>

          {/* Step-by-step instructions */}
          {steps.length > 0 && (
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ClipboardIcon size={16} className="text-floral-700" />
                Step-by-Step POS Instructions
              </h3>

              <ol className="space-y-2">
                {steps.map(({ text, highlight }, index) => (
                  <li
                    key={text}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                      highlight ? 'bg-warn-bg text-warn-fg font-semibold' : 'bg-gray-50 text-gray-900'
                    }`}
                  >
                    <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold tabular ${
                      highlight ? 'bg-warn-fg text-warn-bg' : 'bg-floral-100 text-floral-900'
                    }`}>
                      {index + 1}
                    </span>
                    <span>{text}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* QR Code */}
          <div className="text-center">
            <h4 className="text-[13px] font-semibold text-gray-900 mb-3">QR Code for Quick Scanning</h4>
            {qrCodeUrl ? (
              <div className="inline-block p-4 bg-white border border-gray-200 rounded-lg">
                <img src={qrCodeUrl} alt={`QR code for order ${orderData.orderNumber}`} className="w-48 h-48" />
                <p className="text-xs text-gray-500 mt-2">Scan with POS device</p>
              </div>
            ) : (
              <div className="w-48 h-48 bg-gray-50 flex items-center justify-center rounded-lg mx-auto text-floral-700">
                <LoaderCircleIcon size={28} className="animate-spin" />
              </div>
            )}
          </div>

          {/* Important Note */}
          <div className="banner banner-warn items-start">
            <AlertTriangleIcon size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Important Reminders</p>
              <ul className="font-normal mt-1 space-y-1">
                <li>• Verify order details with customer before processing payment</li>
                <li>• Check delivery date and special instructions</li>
                <li>• Ensure all items are available before finalizing</li>
                <li>• Process payment according to store policy</li>
              </ul>
            </div>
          </div>

          {/* Actions: right-aligned, one primary */}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">
              Close Instructions
            </button>
            <button type="button" onClick={handlePrintInstructions} className="btn-secondary">
              <PrinterIcon size={16} />
              Print Instructions
            </button>
            <button type="button" onClick={handleCopyOrderNumber} className="btn-primary">
              {copySuccess ? (
                <>
                  <CheckIcon size={16} />
                  Copied!
                </>
              ) : (
                <>
                  <CopyIcon size={16} />
                  Copy Order Number
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSInstructions;